import { useEffect, useRef, useState } from "react";
import { ExternalLink } from "lucide-react";
import { useLang } from "@/i18n";
import { capture } from "@/lib/analytics";
import { ROUTES } from "@/config/links";
import content from "./content.json";
import { ARCHETYPE_ACCENTS, CHROME } from "./chrome";
import { drawShareCard, canvasToBlob, canvasToFile, downloadBlob } from "./shareCardImage";

/**
 * New feature (not part of the brief's canonical content): a one-image
 * summary of the result for Instagram / Facebook / LinkedIn. Canvas-
 * generated client-side (no backend round-trip). Where the browser
 * supports the Web Share API with file attachments (most mobile
 * browsers, which is also where IG/FB/LinkedIn app-to-app sharing
 * actually works), a single "Share" button hands the real image to the
 * native share sheet. Elsewhere (desktop), LinkedIn/Facebook's own web
 * share intents don't accept an attached image file — they fall back to
 * sharing the quiz's public link, with an explicit note for Instagram
 * (which has no web share intent at all).
 */
export const ShareCard = ({ model }) => {
  const { lang, l } = useLang();
  const c = CHROME[lang] || CHROME.en;
  const canvasRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [nativeShareSupported, setNativeShareSupported] = useState(false);
  const [shareState, setShareState] = useState("idle"); // idle | sharing | error

  const name = model.isBlend ? model.leaderProfiles.map((p) => p.name).join(" + ") : model.leaderProfiles[0].name;
  const subtitle = model.isBlend ? content.content[lang].ui.blendTitle : model.leaderProfiles[0].label;
  const accentColor = ARCHETYPE_ACCENTS[model.leaders[0]];
  const filename = `InterAct_Presenter_Archetype_Share_${model.language}.png`;
  const shareUrl = `${window.location.origin}${l(ROUTES.presenterArchetype)}`;
  const shareText = c.shareText.replace("{name}", name);

  useEffect(() => {
    let cancelled = false;
    drawShareCard(canvasRef.current, { name, subtitle, accentColor, eyebrow: content.content[lang].ui.tool }).then(() => {
      if (!cancelled) setReady(true);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, subtitle, accentColor, lang]);

  useEffect(() => {
    const dummy = new File([""], filename, { type: "image/png" });
    setNativeShareSupported(
      typeof navigator.share === "function" && typeof navigator.canShare === "function" && navigator.canShare({ files: [dummy] })
    );
  }, [filename]);

  const handleDownload = async () => {
    const blob = await canvasToBlob(canvasRef.current);
    downloadBlob(blob, filename);
    capture("presenter_archetype_share_image_downloaded", { language: model.language, leaders: model.leaders });
  };

  const handleNativeShare = async () => {
    setShareState("sharing");
    try {
      const file = await canvasToFile(canvasRef.current, filename);
      await navigator.share({ files: [file], title: name, text: shareText });
      setShareState("idle");
      capture("presenter_archetype_share_native_completed", { language: model.language, leaders: model.leaders });
    } catch (err) {
      // AbortError = the user simply cancelled the native share sheet.
      if (err?.name === "AbortError") {
        setShareState("idle");
        capture("presenter_archetype_share_native_cancelled", { language: model.language });
      } else {
        setShareState("error");
        capture("presenter_archetype_share_native_error", { language: model.language });
      }
    }
  };

  const handleFallbackClick = (platform) => () =>
    capture("presenter_archetype_share_link_click", { language: model.language, platform, leaders: model.leaders });

  return (
    <div className="rounded-xl border border-black/10 bg-white px-5 py-6 mt-8" data-testid="pa-share-card">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#607e66] mb-1.5">{c.shareEyebrow}</p>
      <h3 className="font-serif text-lg text-[#263b3d] mb-2">{c.shareTitle}</h3>
      <p className="text-sm text-[#3f4a47] mb-4">{c.shareBody}</p>

      <div className="flex flex-col sm:flex-row gap-5 items-start">
        <canvas
          ref={canvasRef}
          data-testid="pa-share-card-canvas"
          className="w-40 h-40 rounded-lg border border-black/10 shrink-0 object-cover"
          style={{ background: "#1c2b2c" }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-3 mb-3">
            <button
              type="button"
              data-testid="pa-share-download-button"
              onClick={handleDownload}
              disabled={!ready}
              className="px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-[#b74e33] hover:bg-[#9c3f29] disabled:opacity-50 transition-colors"
            >
              {c.downloadImageLabel}
            </button>

            {nativeShareSupported ? (
              <button
                type="button"
                data-testid="pa-share-native-button"
                onClick={handleNativeShare}
                disabled={!ready || shareState === "sharing"}
                className="px-5 py-2.5 rounded-full text-sm font-semibold text-[#263b3d] border border-[#263b3d]/20 hover:border-[#263b3d]/40 disabled:opacity-50 transition-colors"
              >
                {c.shareButtonLabel}
              </button>
            ) : (
              <>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pa-share-linkedin-link"
                  onClick={handleFallbackClick("linkedin")}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold text-[#263b3d] border border-[#263b3d]/20 hover:border-[#263b3d]/40 transition-colors"
                >
                  <ExternalLink size={14} /> {c.linkedinShareLabel}
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pa-share-facebook-link"
                  onClick={handleFallbackClick("facebook")}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-semibold text-[#263b3d] border border-[#263b3d]/20 hover:border-[#263b3d]/40 transition-colors"
                >
                  <ExternalLink size={14} /> {c.facebookShareLabel}
                </a>
              </>
            )}
          </div>
          {shareState === "error" && (
            <p className="text-sm text-[#b74e33] mb-2" data-testid="pa-share-error" role="alert">
              {c.shareError}
            </p>
          )}
          {!nativeShareSupported && (
            <p className="text-xs text-[#9a968a]" data-testid="pa-share-instagram-hint">
              {c.instagramHint}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareCard;
